import { resolveActiveAuditOverrides } from './truthAttestations';
/**
 * R-3 — citation-density hard gate (claims / prose, no LLM).
 *
 * Density = citedClaims / totalClaims. When below threshold, generation
 * must regenerate with citation focus or persist an admin override with
 * rationale (≥40 chars).
 */
import { AUDIT_CONFIG } from '../../config/auditConfig';
import type { ReportClaim } from '../../types/citation';
import type { SiteAuditOverride, SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import {
  computeCitationDensity as computeLegacyDensity,
  sentenceHasProseCitation,
  splitSentences,
} from '../citationDensity';
// Type-only import (erased at runtime) — no circular dependency with runFullAudit.
import type { FullAuditFinding } from './runFullAudit';

export const DEFAULT_DENSITY_THRESHOLD = 0.80;
export const OVERRIDE_RATIONALE_MIN_CHARS = 40;

export interface ClaimRef {
  /** 0-based index in the claims[] array (prose mode uses sentence index). */
  index: number;
  text: string;
  sectionId?: string;
}

export interface CitationDensitySnapshot {
  density: number;
  totalClaims: number;
  citedClaims: number;
  uncitedClaims: ClaimRef[];
  threshold: number;
  belowThreshold: boolean;
  source: 'claims' | 'prose';
}

export interface GateDecision {
  pass: boolean;
  snapshot: CitationDensitySnapshot;
  /** Uncited claims blocking publish when `pass === false`. */
  blockingUncited: ClaimRef[];
}

export class CitationDensityGateError extends Error {
  readonly snapshot: CitationDensitySnapshot;

  constructor(snapshot: CitationDensitySnapshot) {
    super(
      `Citation density ${(snapshot.density * 100).toFixed(0)}% `
      + `below threshold ${(snapshot.threshold * 100).toFixed(0)}% `
      + `(${snapshot.citedClaims}/${snapshot.totalClaims} claims cited)`,
    );
    this.name = 'CitationDensityGateError';
    this.snapshot = snapshot;
  }
}

export type CitationDensityReportInput =
  | SiteRagReport
  | {
      claims?: ReportClaim[] | null;
      citationDensity?: SiteRagReport['citationDensity'];
      executiveSummary?: string | null;
      sitingNotes?: string | null;
      summary?: string | null;
      [key: string]: unknown;
    };

export function validateOverrideRationale(rationale: string | null | undefined): boolean {
  return (rationale ?? '').trim().length >= OVERRIDE_RATIONALE_MIN_CHARS;
}

function clampThreshold(x: number): number {
  const min = AUDIT_CONFIG.CITATION_DENSITY_MIN_THRESHOLD;
  if (!Number.isFinite(x)) return DEFAULT_DENSITY_THRESHOLD;
  if (x < min) return min;
  if (x > 1) return 1;
  return x;
}

function extractClaimsFromInput(report: CitationDensityReportInput): ReportClaim[] {
  const raw = (report as { claims?: unknown }).claims;
  if (!Array.isArray(raw)) return [];
  const out: ReportClaim[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as { text?: unknown; sources?: unknown; sectionId?: unknown };
    const text = typeof e.text === 'string' ? e.text.trim() : '';
    if (!text) continue;
    const sourcesRaw = Array.isArray(e.sources) ? e.sources : [];
    const sources = sourcesRaw
      .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
      .map(s => s.trim());
    const sectionId = typeof e.sectionId === 'string' && e.sectionId.trim()
      ? e.sectionId.trim()
      : undefined;
    const claim: ReportClaim = { text, sources };
    if (sectionId) claim.sectionId = sectionId;
    out.push(claim);
  }
  return out;
}

function collectProseTexts(report: CitationDensityReportInput): string[] {
  const r = report as Record<string, unknown>;
  const texts: Array<string | null | undefined> = [
    typeof r.executiveSummary === 'string' ? r.executiveSummary : null,
    typeof r.sitingNotes === 'string' ? r.sitingNotes : null,
    typeof r.summary === 'string' ? r.summary : null,
  ];
  return texts.filter((t): t is string => typeof t === 'string' && t.trim().length > 0);
}

function uncitedFromClaims(claims: ReportClaim[]): ClaimRef[] {
  const out: ClaimRef[] = [];
  claims.forEach((c, index) => {
    if (c.sources.length > 0) return;
    out.push({
      index,
      text: c.text,
      ...(c.sectionId ? { sectionId: c.sectionId } : {}),
    });
  });
  return out;
}

function uncitedFromProse(texts: string[]): ClaimRef[] {
  const out: ClaimRef[] = [];
  let index = 0;
  for (const t of texts) {
    for (const sentence of splitSentences(t)) {
      if (!sentenceHasProseCitation(sentence)) {
        out.push({ index, text: sentence });
      }
      index += 1;
    }
  }
  return out;
}

/**
 * Compute citation density for a report or in-flight gemini payload.
 * Prefers structured `claims[]`; falls back to prose `[Ref N]` scan.
 */
export function computeCitationDensity(
  report: CitationDensityReportInput,
  threshold: number = AUDIT_CONFIG.CITATION_DENSITY_DEFAULT_THRESHOLD,
): CitationDensitySnapshot {
  const floor = clampThreshold(threshold);
  const claims = extractClaimsFromInput(report);
  const proseTexts = collectProseTexts(report);
  const legacy = computeLegacyDensity(
    { claims: claims.length > 0 ? claims : null, proseTexts },
    floor,
  );

  const uncitedClaims = claims.length > 0
    ? uncitedFromClaims(claims)
    : uncitedFromProse(proseTexts);

  return {
    density: legacy.density,
    totalClaims: legacy.totalSentences,
    citedClaims: legacy.citedSentences,
    uncitedClaims,
    threshold: floor,
    belowThreshold: legacy.belowFloor,
    source: legacy.source,
  };
}

function hasPersistedCitationDensityOverride(
  auditOverrides: SiteAuditOverride[] | undefined,
  reportTypeId?: string,
): boolean {
  for (const ov of resolveActiveAuditOverrides(auditOverrides)) {
    if ('gate' in ov && ov.gate === 'citation-density') {
      if (ov.reportTypeId && reportTypeId && ov.reportTypeId !== reportTypeId) continue;
      if (validateOverrideRationale(ov.rationale)) return true;
    }
    if ('type' in ov && ov.type === 'density') {
      if (ov.reportTypeId && reportTypeId && ov.reportTypeId !== reportTypeId) continue;
      if (validateOverrideRationale(ov.rationale)) return true;
    }
  }
  return false;
}

export interface EvaluateCitationDensityGateOptions {
  threshold?: number;
  auditOverrides?: SiteAuditOverride[];
  reportTypeId?: string;
  /** One-shot dispatch waiver (banner retry path). */
  allowOverride?: boolean;
  rationale?: string;
  /** When false, always returns pass (warn-only / rollout). */
  gateEnabled?: boolean;
}

/** Evaluate whether density meets the floor, honouring overrides. */
export function evaluateCitationDensityGate(
  report: CitationDensityReportInput,
  opts?: EvaluateCitationDensityGateOptions,
): GateDecision {
  const snapshot = computeCitationDensity(report, opts?.threshold);
  const gateEnabled = opts?.gateEnabled ?? AUDIT_CONFIG.CITATION_DENSITY_GATE_ENABLED;
  const waived = opts?.allowOverride
    || validateOverrideRationale(opts?.rationale)
    || hasPersistedCitationDensityOverride(opts?.auditOverrides, opts?.reportTypeId);

  const pass = !gateEnabled || !snapshot.belowThreshold || waived;
  return {
    pass,
    snapshot,
    blockingUncited: pass ? [] : snapshot.uncitedClaims,
  };
}

/** Build MUST CITE focus block for the next review / regen prompt. */
export function buildMustCiteFocusBlock(uncitedClaims: readonly ClaimRef[]): string {
  if (uncitedClaims.length === 0) return '';
  const lines = [
    'MUST CITE: The following claims lack source attachments. Add [Ref N] tokens or populate claims[].sources for each before finishing:',
  ];
  for (const c of uncitedClaims.slice(0, 20)) {
    lines.push(`- ${c.text}`);
  }
  if (uncitedClaims.length > 20) {
    lines.push(`- …and ${uncitedClaims.length - 20} more uncited claim(s).`);
  }
  return lines.join('\n');
}

export function assertCitationDensityGate(
  report: CitationDensityReportInput,
  opts?: EvaluateCitationDensityGateOptions,
): GateDecision {
  const decision = evaluateCitationDensityGate(report, opts);
  if (!decision.pass) {
    throw new CitationDensityGateError(decision.snapshot);
  }
  return decision;
}

/**
 * Part C SP3 (GAP-1) — single source of truth for the R-3 citation-density
 * audit finding. BOTH runFullAudit and composePostGenAudit call this with the
 * SAME report + site so the finding they emit is byte-identical, which the
 * pipelineParity exact-`toEqual` test depends on.
 *
 * Severity is `warn`, NOT `block`: a density shortfall is a quality signal
 * (the publish gate blocks only on `block` findings), so this surfaces in
 * `warningFindings` rather than hard-blocking publish. Generation already
 * enforces the floor via assertCitationDensityGate (reportQualityPipeline
 * Phase 5); this is the publish-time backstop / surfacing, not a second hard
 * gate.
 *
 * `totalClaims === 0` returns null (emit nothing): a persisted report with no
 * claims[] AND no executiveSummary/sitingNotes/summary prose would score a
 * misleading 0% density off an empty corpus. We skip rather than mislead.
 *
 * Per-company threshold drift (v1): we deliberately use the STATIC
 * AUDIT_CONFIG.CITATION_DENSITY_DEFAULT_THRESHOLD here instead of the async
 * per-company floor (loadCitationDensityThreshold), because this runs inside
 * the synchronous detector pipelines and an async Firestore read would change
 * their contract. RESULT: generation may enforce a per-company floor while the
 * publish-time R-3 check uses the platform default. Threading the per-company
 * floor through is a deferred v2 follow-up.
 */
export const CITATION_DENSITY_FINDING_KIND = 'CITATION_DENSITY_BELOW_THRESHOLD';

export function evaluateCitationDensityFinding(
  report: CitationDensityReportInput,
  site: Pick<SiteEntity, 'auditOverrides'> | null | undefined,
  reportTypeId?: string,
): FullAuditFinding | null {
  const decision = evaluateCitationDensityGate(report, {
    threshold: AUDIT_CONFIG.CITATION_DENSITY_DEFAULT_THRESHOLD,
    gateEnabled: AUDIT_CONFIG.CITATION_DENSITY_GATE_ENABLED,
    auditOverrides: site?.auditOverrides,
    ...(reportTypeId ? { reportTypeId } : {}),
  });

  // totalClaims === 0 → emit NOTHING (would otherwise read a misleading 0%).
  if (decision.snapshot.totalClaims === 0) return null;

  // Gate honours the configured floor + persisted/admin overrides. When it
  // passes (above floor, gate disabled, or waived) there is nothing to surface.
  if (decision.pass) return null;

  const { density, threshold, citedClaims, totalClaims } = decision.snapshot;
  return {
    detectorId: 'R-3',
    severity: 'warn',
    kind: CITATION_DENSITY_FINDING_KIND,
    message:
      `Citation density ${(density * 100).toFixed(0)}% below threshold `
      + `${(threshold * 100).toFixed(0)}% (${citedClaims}/${totalClaims} claims cited).`,
  };
}

/* ── R-3 citation-density fixes (2026-06-10) — helper exports only.
 *    No threshold or gate-behaviour change in this block.            */

/**
 * Counting twins of `REF_TOKEN_PROBE` / `RELIABILITY_TOKEN_PROBE` in
 * `../citationDensity` (those are boolean probes; these are global so
 * `.match()` returns every occurrence). Keep the patterns in sync —
 * `sentenceHasProseCitation(text) === (countCitationTokens(text) > 0)`
 * is pinned by test.
 */
const REF_TOKEN_COUNTER = /\[Ref \d+\]/g;
const RELIABILITY_TOKEN_COUNTER = /\[(?:verified|inferred|unverified)(?:\s+[^\]]+)?\]/gi;

/** Count `[Ref N]` + reliability-tag tokens in a narrative string. */
export function countCitationTokens(text: string | null | undefined): number {
  if (typeof text !== 'string' || text.length === 0) return 0;
  const refs = text.match(REF_TOKEN_COUNTER)?.length ?? 0;
  const reliability = text.match(RELIABILITY_TOKEN_COUNTER)?.length ?? 0;
  return refs + reliability;
}

/**
 * Anti-regression guard for reviewer corrections (fix 4): a replacement
 * may not carry FEWER citation tokens than the text it replaces. The
 * reviewer's `correctedSections` demonstrably dropped `[Ref N]` tokens
 * while shortening sections (Holly Ridge job rgj-1781052906496: exec
 * summary 12→6 sentences, cited count stuck at 1), driving prose-mode
 * density DOWN during "repair". Non-string inputs pass — object-shaped
 * corrections are out of scope for the token guard.
 */
export function correctionPreservesCitations(prev: unknown, next: unknown): boolean {
  if (typeof prev !== 'string' || typeof next !== 'string') return true;
  return countCitationTokens(next) >= countCitationTokens(prev);
}

/**
 * Generation-aimed citation-density retry overlay (fix 3). Unlike
 * `buildMustCiteFocusBlock` (a bare uncited list, historically injected
 * into the REVIEWER's SED — a model whose response schema has no
 * `claims` field and which can only replace five string sections), this
 * overlay is prepended to the GENERATOR's composedContext so the model
 * that owns the narrative + `claims[]` repairs the citing at the source.
 *
 * Works with an empty uncited list (the banner's regenerate-with-focus
 * dispatch carries only a boolean — the failed snapshot is not threaded
 * through the retry request): the contract preamble alone tells the
 * generator exactly what the gate measures. The in-run density retry
 * passes the failed snapshot's uncited claims for targeted focus.
 */
export function buildCitationDensityRetryOverlay(
  uncitedClaims: readonly ClaimRef[] = [],
): string {
  const lines = [
    'CITATION DENSITY RETRY (HARD GATE — a previous attempt failed):',
    'The prior generation failed the citation-density floor: the share of claims[] entries carrying a non-empty sources[] array must meet the configured floor (default 80%).',
    '- Emit the REQUIRED top-level `claims` array covering EVERY major section — one entry per load-bearing factual sentence.',
    '- Attach at least one source to every claim: `ref:N` for SOURCES-list facts; `sed:<dot.path>` / `sed-section:<id>` / `site-context:<id>` for intent-derived or platform-computed planning figures.',
    '- Tag intent-derived planning sentences with [verified sed-section:<id>] or [inferred planning default] — NEVER a fabricated [Ref N].',
  ];
  const focus = buildMustCiteFocusBlock(uncitedClaims);
  if (focus) lines.push('', focus);
  return `${lines.join('\n')}\n\n`;
}
